import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { apiSettingsCardSx } from '../../../constants/apiSettingsCardTypography';
import {
  fetchSftpCredentials,
  useGetSftpSettingsHandler,
  useSaveSftpConnectionHandler,
} from '../dataHandlers/sftpSettingsHandlers';
import SftpConnectionDialog from './SftpConnectionDialog';

const SftpConnectionCard = () => {
  const { pollIntervalSeconds, maxConcurrentFiles, isLoading } = useGetSftpSettingsHandler();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState('create');
  const [credentialsLoading, setCredentialsLoading] = React.useState(true);
  const [username, setUsername] = React.useState('');
  const [workerPublicKey, setWorkerPublicKey] = React.useState('');
  const [hasExistingCredentials, setHasExistingCredentials] = React.useState(false);

  const isConfigurationComplete = hasExistingCredentials;

  React.useEffect(() => {
    let cancelled = false;
    setCredentialsLoading(true);
    fetchSftpCredentials()
      .then((data) => {
        if (cancelled) return;
        const nextUsername = data?.username || '';
        setUsername(nextUsername);
        setWorkerPublicKey(data?.worker_public_key || '');
        setHasExistingCredentials(Boolean(nextUsername));
      })
      .catch(() => {
        if (cancelled) return;
        setUsername('');
        setWorkerPublicKey('');
        setHasExistingCredentials(false);
      })
      .finally(() => {
        if (!cancelled) setCredentialsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const { onSubmit, isPending } = useSaveSftpConnectionHandler({
    onSuccess: (data) => {
      setDialogOpen(false);
      if (data?.username) {
        setUsername(data.username);
        setHasExistingCredentials(true);
      }
      fetchSftpCredentials()
        .then((data) => setWorkerPublicKey(data?.worker_public_key || ''))
        .catch(() => setWorkerPublicKey(''));
    },
  });

  const openCreate = () => {
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openEdit = () => {
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const openView = () => {
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleSwitchToEdit = () => {
    setDialogMode('edit');
  };

  const handleSave = ({ username: nextUsername, password, poll_interval_seconds, max_concurrent_files }) => {
    if (isLoading) return;

    onSubmit({
      username: nextUsername,
      password,
      poll_interval_seconds,
      max_concurrent_files,
    });
  };

  return (
    <Stack spacing={1.5}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <VpnKeyOutlinedIcon fontSize="small" color="primary" />
          <Typography sx={apiSettingsCardSx.title}>SFTP configuration</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={apiSettingsCardSx.description}>
          One SFTP user per organization with shared poll settings. <code>sent_inbox</code> accepts both{' '}
          <code>.json</code> and <code>.xlsx</code>; routing uses <code>connector_key</code> in each file.
        </Typography>
      </Box>

      {!isConfigurationComplete && !credentialsLoading && (
        <Stack spacing={1.5} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            No SFTP configuration yet. Create one to set credentials and ingestion poll settings.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            sx={apiSettingsCardSx.button}
            disabled={isLoading}
            onClick={openCreate}
          >
            Create configuration
          </Button>
        </Stack>
      )}

      {isConfigurationComplete && (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Poll interval</TableCell>
                <TableCell>Max concurrent</TableCell>
                <TableCell>Worker SSH key</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow hover>
                <TableCell>{username || '—'}</TableCell>
                <TableCell>{pollIntervalSeconds ?? 60}s</TableCell>
                <TableCell>{maxConcurrentFiles ?? 2}</TableCell>
                <TableCell>{workerPublicKey ? 'Generated' : '—'}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={openView}
                    aria-label="View SFTP configuration"
                    disabled={credentialsLoading}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={openEdit}
                    aria-label="Edit SFTP configuration"
                    disabled={credentialsLoading}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SftpConnectionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSave}
        isPending={isPending}
        mode={dialogMode}
        initialUsername={username}
        workerPublicKey={workerPublicKey}
        hasExistingCredentials={hasExistingCredentials}
        initialPollIntervalSeconds={pollIntervalSeconds}
        initialMaxConcurrentFiles={maxConcurrentFiles}
        onSwitchToEdit={dialogMode === 'view' ? handleSwitchToEdit : undefined}
      />
    </Stack>
  );
};

export default SftpConnectionCard;
